import { EventEmitter, Container, Graphics, Text } from "pixi.js";
import GameAbstract from "../game-abstract";
import { BUTTON_TYPE } from "../../../game-boy/data/game-boy-data";
import { GAME_BOY_CONFIG } from "../../../game-boy/data/game-boy-config";

const SCREEN_W = GAME_BOY_CONFIG.screen.width;   // 160
const SCREEN_H = GAME_BOY_CONFIG.screen.height;  // 144

enum STEP {
  Title       = 0,
  Intro       = 1,
  Era1Choice  = 2,
  Era1Out     = 3,
  Era2Choice  = 4,
  Era2Out     = 5,
  Era3Choice  = 6,
  Era3Out     = 7,
  Era4Choice  = 8,
  Era4Out     = 9,
  Nixon       = 10,
  Dilution    = 11,
  FinalChoice = 12,
  FinalOut    = 13,
  Score       = 14,
  Menu        = 15,
}

type Asset = 'CHICKENS' | 'COWS' | 'SHELLS' | 'GOLD' | 'DOLLARS' |
             'BUSINESS' | 'STOCKS' | 'BITCOIN' | 'XRP';

const FINAL_OPTIONS: { key: Asset; label: string }[] = [
  { key: 'DOLLARS',  label: 'HOLD DOLLARS' },
  { key: 'GOLD',     label: 'BUY GOLD' },
  { key: 'BUSINESS', label: 'START BUSINESS' },
  { key: 'STOCKS',   label: 'BUY STOCKS' },
  { key: 'BITCOIN',  label: 'BUY BITCOIN' },
  { key: 'XRP',      label: 'BUY XRP' },
];

const FINAL_OUTCOMES: Record<Asset, { lines: string[]; deltaPct: number }> = {
  CHICKENS:  { lines: [], deltaPct: 0 },
  COWS:      { lines: [], deltaPct: 0 },
  SHELLS:    { lines: [], deltaPct: 0 },
  DOLLARS: {
    lines: ['Dollar bleeds -30%', 'in 4 years.', 'Predictable. Easy.', 'But you pay the', 'inflation tax.'],
    deltaPct: -30,
  },
  GOLD: {
    lines: ['+60%. Held value.', 'But you can\'t pay', 'rent with it.', 'Hard to send to a', 'friend overseas.'],
    deltaPct: 60,
  },
  BUSINESS: {
    lines: ['+200% IF it works.', '60% of businesses', 'fail in 5 years.', 'Hardest mode,', 'biggest reward.'],
    deltaPct: 50,
  },
  STOCKS: {
    lines: ['+80%. You own real', 'productive things.', 'But companies print', 'new shares. Your slice', 'shrinks even sitting.'],
    deltaPct: 80,
  },
  BITCOIN: {
    lines: ['+300%. Only 21M', 'will EVER exist.', 'But settles in ~10 min.', 'Fees spike when busy.', 'Digital gold.'],
    deltaPct: 300,
  },
  XRP: {
    lines: ['+150%. Fixed 100B.', 'Settles in 3 seconds.', 'Cross-border, no', 'permission. Send to', 'Mars before a wire', 'transfer clears.'],
    deltaPct: 150,
  },
};

type SpriteKind = 'none' | 'chickens_cows' | 'shells' | 'gold' | 'bank' | 'tv' |
                  'pizza' | 'question' | 'trophy';

export default class Oryn extends GameAbstract {
  public events: EventEmitter;

  private isBuilt: boolean = false;
  private step: STEP = STEP.Title;
  private wealth: number = 100;
  private currentAsset: Asset = 'CHICKENS';
  private lastChoice: 'A' | 'B' | null = null;
  private finalCursor: number = 5;
  private finalChoice: Asset | null = null;
  private blinkTime: number = 0;

  // UI
  private eraLabel!: Text;
  private titleText!: Text;
  private bodyLines: Text[] = [];
  private promptText!: Text;
  private wealthText!: Text;
  private optionTexts: Text[] = [];
  private sceneSprite!: Graphics;

  constructor() {
    super();
    this.events = new EventEmitter();
    this.visible = false;
  }

  public show(): void {
    if (!this.isBuilt) this.build();
    this.step = STEP.Title;
    this.wealth = 100;
    this.currentAsset = 'CHICKENS';
    this.lastChoice = null;
    this.finalCursor = 5;
    this.finalChoice = null;
    this.blinkTime = 0;
    this.render();
    super.show();
  }

  public update(dt: number): void {
    this.blinkTime += dt;
    if (this.step === STEP.Title) {
      this.promptText.visible = Math.floor(this.blinkTime * 2) % 2 === 0;
    }
  }

  public onButtonPress(buttonType: BUTTON_TYPE): void {
    const isAdvanceStep = [
      STEP.Title, STEP.Intro, STEP.Era1Out, STEP.Era2Out, STEP.Era3Out,
      STEP.Era4Out, STEP.Nixon, STEP.Dilution, STEP.FinalOut, STEP.Score,
    ].includes(this.step);

    if (isAdvanceStep) {
      if (buttonType === BUTTON_TYPE.A || buttonType === BUTTON_TYPE.Start) {
        this.step = (this.step + 1) as STEP;
        this.applyForcedEvents();
        this.render();
      }
      return;
    }

    if (this.isBinaryChoiceStep(this.step)) {
      if (buttonType === BUTTON_TYPE.A || buttonType === BUTTON_TYPE.B) {
        this.lastChoice = buttonType === BUTTON_TYPE.A ? 'A' : 'B';
        this.applyBinaryChoice();
        this.step = (this.step + 1) as STEP;
        this.render();
      }
      return;
    }

    if (this.step === STEP.FinalChoice) {
      if (buttonType === BUTTON_TYPE.CrossUp) {
        this.finalCursor = (this.finalCursor + FINAL_OPTIONS.length - 1) % FINAL_OPTIONS.length;
        this.render();
      } else if (buttonType === BUTTON_TYPE.CrossDown) {
        this.finalCursor = (this.finalCursor + 1) % FINAL_OPTIONS.length;
        this.render();
      } else if (buttonType === BUTTON_TYPE.A || buttonType === BUTTON_TYPE.Start) {
        this.finalChoice = FINAL_OPTIONS[this.finalCursor].key;
        this.currentAsset = this.finalChoice;
        const outcome = FINAL_OUTCOMES[this.finalChoice];
        this.wealth = Math.max(0, Math.round(this.wealth * (1 + outcome.deltaPct / 100)));
        (window as any).track && (window as any).track('long_money_hedge_chosen', { choice: this.finalChoice });
        this.step = STEP.FinalOut;
        this.render();
      }
      return;
    }

    if (this.step === STEP.Menu) {
      // Only one option now: PLAY AGAIN. A/Start restarts the game.
      if (buttonType === BUTTON_TYPE.A || buttonType === BUTTON_TYPE.Start) {
        (window as any).track && (window as any).track('long_money_replay');
        this.show();
      }
    }
  }

  public onButtonUp(): void { }

  private isBinaryChoiceStep(step: STEP): boolean {
    return step === STEP.Era1Choice || step === STEP.Era2Choice ||
           step === STEP.Era3Choice || step === STEP.Era4Choice;
  }

  private applyBinaryChoice(): void {
    const A = this.lastChoice === 'A';
    switch (this.step) {
      case STEP.Era1Choice:
        if (A) { this.currentAsset = 'COWS';     this.wealth = Math.round(this.wealth * 0.70); }
        else   { this.currentAsset = 'CHICKENS'; this.wealth = Math.round(this.wealth * 0.60); }
        break;
      case STEP.Era2Choice:
        if (A) { this.currentAsset = 'SHELLS';   this.wealth = Math.round(this.wealth * 0.85); }
        else   { this.wealth = Math.round(this.wealth * 0.60); }
        break;
      case STEP.Era3Choice:
        if (A) { this.currentAsset = 'GOLD';     this.wealth = Math.round(this.wealth * 1.20); }
        else   { this.wealth = Math.round(this.wealth * 0.70); }
        break;
      case STEP.Era4Choice:
        if (A) { this.currentAsset = 'DOLLARS';  this.wealth = Math.round(this.wealth * 1.10); }
        else   { this.wealth = Math.round(this.wealth * 0.95); }
        break;
    }
  }

  private applyForcedEvents(): void {
    if (this.step === STEP.Nixon) {
      if (this.currentAsset === 'DOLLARS')      this.wealth = Math.round(this.wealth * 0.85);
      else if (this.currentAsset === 'GOLD')    this.wealth = Math.round(this.wealth * 1.40);
    } else if (this.step === STEP.Dilution) {
      if (this.currentAsset === 'DOLLARS')      this.wealth = Math.round(this.wealth * 0.75);
    }
  }

  // ---------- Sprite drawer ----------

  // Draws a simple pixel sprite centered around (cx, cy). Keeps everything
  // in PixiJS Graphics primitives so we don't need any external assets.
  private drawSprite(kind: SpriteKind): void {
    const g = this.sceneSprite;
    g.clear();
    if (kind === 'none') { g.visible = false; return; }
    g.visible = true;

    const cx = SCREEN_W / 2;
    const cy = 38;

    // Helper: thin black line as a tiny rectangle. Avoids the moveTo/lineTo
    // stroke quirk that was leaving stray paths from (0,0) to each new arc.
    const line = (x1: number, y1: number, x2: number, y2: number, color = 0x000000) => {
      const dx = x2 - x1, dy = y2 - y1;
      const len = Math.sqrt(dx*dx + dy*dy);
      if (len < 0.5) return;
      // Approximate as a thin oriented rect (or use poly for diagonal)
      if (Math.abs(dx) < 0.5) { g.rect(x1, Math.min(y1,y2), 1, len).fill(color); return; }
      if (Math.abs(dy) < 0.5) { g.rect(Math.min(x1,x2), y1, len, 1).fill(color); return; }
      // Diagonal: use a thin polygon
      const nx = -dy/len, ny = dx/len;
      g.poly([
        x1 + nx*0.5, y1 + ny*0.5,
        x2 + nx*0.5, y2 + ny*0.5,
        x2 - nx*0.5, y2 - ny*0.5,
        x1 - nx*0.5, y1 - ny*0.5,
      ]).fill(color);
    };

    switch (kind) {
      case 'chickens_cows': {
        // === Detailed chicken on the LEFT ===
        const lx = cx - 22;
        // Body
        g.ellipse(lx, cy + 2, 7, 5).fill(0xffffff).stroke({ width: 1, color: 0x000000 });
        // Head (round)
        g.circle(lx + 5, cy - 4, 4).fill(0xffffff).stroke({ width: 1, color: 0x000000 });
        // Beak
        g.poly([lx + 8, cy - 4, lx + 12, cy - 3, lx + 8, cy - 2]).fill(0xffa500).stroke({ width: 1, color: 0x000000 });
        // Comb (red)
        g.poly([lx + 4, cy - 8, lx + 5, cy - 10, lx + 6, cy - 8, lx + 7, cy - 10, lx + 7, cy - 8]).fill(0xcc0000);
        // Eye
        g.circle(lx + 6, cy - 5, 1).fill(0x000000);
        // Wing
        g.ellipse(lx, cy + 2, 3, 2).fill(0xeeeeee).stroke({ width: 1, color: 0x000000 });
        // Legs
        g.rect(lx - 1, cy + 6, 1, 4).fill(0xffa500);
        g.rect(lx + 2, cy + 6, 1, 4).fill(0xffa500);
        // Feet
        g.rect(lx - 3, cy + 10, 4, 1).fill(0xffa500);
        g.rect(lx, cy + 10, 4, 1).fill(0xffa500);

        // === Detailed cow on the RIGHT (bigger) ===
        const rx = cx + 8;
        // Body
        g.rect(rx, cy - 3, 20, 10).fill(0xffffff).stroke({ width: 1, color: 0x000000 });
        // Black spots (Holstein)
        g.ellipse(rx + 4, cy + 1, 3, 2).fill(0x000000);
        g.ellipse(rx + 13, cy + 3, 3, 2).fill(0x000000);
        g.ellipse(rx + 16, cy - 1, 2, 1.5).fill(0x000000);
        // Head
        g.rect(rx - 5, cy - 6, 7, 6).fill(0xffffff).stroke({ width: 1, color: 0x000000 });
        // Eye
        g.circle(rx - 3, cy - 4, 1).fill(0x000000);
        // Nostrils
        g.circle(rx - 3, cy - 1, 0.7).fill(0x000000);
        g.circle(rx - 1, cy - 1, 0.7).fill(0x000000);
        // Horns
        g.poly([rx - 5, cy - 6, rx - 7, cy - 9, rx - 4, cy - 6]).fill(0xeeddaa).stroke({ width: 1, color: 0x000000 });
        g.poly([rx, cy - 6, rx + 2, cy - 9, rx + 1, cy - 6]).fill(0xeeddaa).stroke({ width: 1, color: 0x000000 });
        // Legs
        g.rect(rx + 1, cy + 7, 2, 4).fill(0xffffff).stroke({ width: 1, color: 0x000000 });
        g.rect(rx + 16, cy + 7, 2, 4).fill(0xffffff).stroke({ width: 1, color: 0x000000 });
        // Tail
        g.rect(rx + 20, cy - 2, 1, 6).fill(0xffffff).stroke({ width: 1, color: 0x000000 });
        break;
      }
      case 'shells': {
        // 3 cowry-style shells — use stacked ellipses (closed shapes, no arcs)
        for (let i = 0; i < 3; i++) {
          const x = cx - 16 + i * 14;
          // Outer shell (oval)
          g.ellipse(x, cy, 6, 7).fill(0xffd9b3).stroke({ width: 1, color: 0x000000 });
          // Inner darker ridge
          g.ellipse(x, cy, 4, 5).fill(0xeeb888);
          // Slot in middle
          g.ellipse(x, cy, 1, 4).fill(0x884422);
        }
        break;
      }
      case 'gold': {
        // 3 stacked gold coins
        for (let i = 0; i < 3; i++) {
          const y = cy + 4 - i * 5;
          g.ellipse(cx, y, 14, 4).fill(0xffd700).stroke({ width: 1, color: 0x000000 });
        }
        g.ellipse(cx, cy - 6, 14, 4).fill(0xffe55c).stroke({ width: 1, color: 0x000000 });
        // Tiny $ on top coin
        g.rect(cx - 1, cy - 9, 2, 6).fill(0x886600);
        g.rect(cx - 3, cy - 8, 6, 1).fill(0x886600);
        g.rect(cx - 3, cy - 4, 6, 1).fill(0x886600);
        break;
      }
      case 'bank': {
        g.poly([cx - 22, cy - 4, cx, cy - 14, cx + 22, cy - 4]).fill(0xeeeeee).stroke({ width: 1, color: 0x000000 });
        g.rect(cx - 20, cy - 4, 40, 14).fill(0xffffff).stroke({ width: 1, color: 0x000000 });
        for (let i = 0; i < 4; i++) {
          const x = cx - 17 + i * 11;
          g.rect(x, cy - 2, 4, 10).fill(0xcccccc).stroke({ width: 1, color: 0x000000 });
        }
        g.rect(cx - 22, cy + 10, 44, 3).fill(0xaaaaaa).stroke({ width: 1, color: 0x000000 });
        break;
      }
      case 'tv': {
        g.rect(cx - 18, cy - 10, 36, 22).fill(0x666666).stroke({ width: 1, color: 0x000000 });
        g.rect(cx - 15, cy - 7, 30, 16).fill(0xeeeeee).stroke({ width: 1, color: 0x000000 });
        // Figure silhouette
        g.circle(cx, cy - 2, 3).fill(0x333333);
        g.rect(cx - 4, cy + 2, 8, 6).fill(0x333333);
        // Antennas — drawn as thin diagonal polygons (not strokes — those leak)
        line(cx - 6, cy - 10, cx - 12, cy - 18);
        line(cx + 6, cy - 10, cx + 12, cy - 18);
        // Stand
        g.rect(cx - 4, cy + 12, 8, 3).fill(0x666666).stroke({ width: 1, color: 0x000000 });
        break;
      }
      case 'pizza': {
        const r = 11;
        // LEFT pizza: 6 slices
        g.circle(cx - 18, cy, r).fill(0xffcc66).stroke({ width: 1, color: 0x000000 });
        for (let i = 0; i < 6; i++) {
          const a = (i * Math.PI) / 3;
          line(cx - 18, cy, cx - 18 + Math.cos(a) * r, cy + Math.sin(a) * r, 0x884400);
        }
        // RIGHT pizza: 14 slices
        g.circle(cx + 18, cy, r).fill(0xffcc66).stroke({ width: 1, color: 0x000000 });
        for (let i = 0; i < 14; i++) {
          const a = (i * Math.PI * 2) / 14;
          line(cx + 18, cy, cx + 18 + Math.cos(a) * r, cy + Math.sin(a) * r, 0x884400);
        }
        // Arrow between
        g.poly([cx - 4, cy - 2, cx + 4, cy - 2, cx + 4, cy - 4, cx + 7, cy, cx + 4, cy + 4, cx + 4, cy + 2, cx - 4, cy + 2]).fill(0x000000);
        break;
      }
      case 'question': {
        g.rect(cx - 12, cy - 12, 24, 24).fill(0xffffff).stroke({ width: 1, color: 0x000000 });
        // "$" left
        g.rect(cx - 9, cy - 8, 2, 16).fill(0x000000);
        g.rect(cx - 11, cy - 6, 6, 2).fill(0x000000);
        g.rect(cx - 11, cy - 2, 6, 2).fill(0x000000);
        g.rect(cx - 11, cy + 2, 6, 2).fill(0x000000);
        // "?" right
        g.rect(cx + 1, cy - 8, 6, 2).fill(0x000000);
        g.rect(cx + 5, cy - 6, 2, 4).fill(0x000000);
        g.rect(cx + 3, cy - 2, 2, 2).fill(0x000000);
        g.rect(cx + 3, cy + 4, 2, 2).fill(0x000000);
        break;
      }
      case 'trophy': {
        // Cup base + stem
        g.rect(cx - 10, cy + 4, 20, 5).fill(0xc0c0c0).stroke({ width: 1, color: 0x000000 });
        g.rect(cx - 4, cy + 9, 8, 3).fill(0x808080).stroke({ width: 1, color: 0x000000 });
        // Cup body (trapezoid)
        g.poly([cx - 12, cy - 12, cx + 12, cy - 12, cx + 10, cy + 4, cx - 10, cy + 4]).fill(0xffd700).stroke({ width: 1, color: 0x000000 });
        // Handles — drawn as closed shapes (NO arcs to avoid stray paths)
        g.poly([cx - 12, cy - 8, cx - 16, cy - 6, cx - 16, cy - 2, cx - 12, cy]).fill(0xffd700).stroke({ width: 1, color: 0x000000 });
        g.poly([cx + 12, cy - 8, cx + 16, cy - 6, cx + 16, cy - 2, cx + 12, cy]).fill(0xffd700).stroke({ width: 1, color: 0x000000 });
        // Star on cup
        g.poly([cx, cy - 9, cx + 1.5, cy - 5, cx + 5, cy - 5, cx + 2, cy - 3, cx + 3, cy + 1, cx, cy - 1, cx - 3, cy + 1, cx - 2, cy - 3, cx - 5, cy - 5, cx - 1.5, cy - 5]).fill(0xffaa00);
        break;
      }
    }
  }

  // Returns the sprite kind for the current step (or 'none' if no sprite).
  private spriteFor(step: STEP): SpriteKind {
    switch (step) {
      case STEP.Era1Choice:
      case STEP.Era1Out:
        return 'chickens_cows';
      case STEP.Era2Choice:
      case STEP.Era2Out:
        return 'shells';
      case STEP.Era3Choice:
      case STEP.Era3Out:
        return 'gold';
      case STEP.Era4Choice:
      case STEP.Era4Out:
        return 'bank';
      case STEP.Nixon:
        return 'tv';
      case STEP.Dilution:
        return 'pizza';
      case STEP.FinalChoice:
        return 'question';
      case STEP.Score:
        return 'trophy';
      default:
        return 'none';
    }
  }

  // ---------- Render ----------

  private render(): void {
    this.eraLabel.visible = true;
    this.titleText.visible = false;
    this.bodyLines.forEach((t, i) => { t.visible = false; t.text = ''; t.y = 70 + i * 10; });
    this.promptText.visible = true;
    this.wealthText.visible = true;
    this.optionTexts.forEach(t => { t.visible = false; t.text = ''; });

    const sprite = this.spriteFor(this.step);
    this.drawSprite(sprite);

    // When a sprite is shown, body sits BELOW it (y=70). Otherwise body sits higher (y=20).
    const bodyStartY = sprite === 'none' ? 20 : 70;
    this.bodyLines.forEach((t, i) => { t.y = bodyStartY + i * 10; });

    const setBody = (lines: string[]) => {
      for (let i = 0; i < this.bodyLines.length; i++) {
        const t = this.bodyLines[i];
        if (i < lines.length) {
          t.text = lines[i];
          t.visible = true;
        }
      }
    };

    const setOptions = (labels: string[], cursorIdx: number, startY: number = 50) => {
      for (let i = 0; i < this.optionTexts.length; i++) {
        const t = this.optionTexts[i];
        if (i < labels.length) {
          t.text = (i === cursorIdx ? '> ' : '  ') + labels[i];
          t.y = startY + i * 11;
          t.visible = true;
        }
      }
    };

    this.wealthText.text = `$${this.wealth}`;

    switch (this.step) {
      case STEP.Title:
        this.eraLabel.visible = false;
        this.titleText.visible = true;
        this.titleText.text = 'LONG MONEY';
        this.titleText.y = 24;
        // Body sits BELOW the title, not overlapping it.
        setBody(['survive 12,000 years', 'with your wealth.', '', 'a game by ric.']);
        this.bodyLines.forEach((t, i) => { t.y = 60 + i * 11; });
        this.promptText.text = 'press A';
        this.wealthText.visible = false;
        break;

      case STEP.Intro:
        this.eraLabel.text = 'YOUR MISSION';
        setBody([
          'You start with $100.',
          'Each era you choose',
          'what to trade for.',
          'Wrong choices lose',
          'wealth. Right choices',
          'preserve it.',
        ]);
        this.promptText.text = 'A: begin';
        break;

      case STEP.Era1Choice:
        this.eraLabel.text = '9000 BC - BARTER';
        setBody([
          'You: 100 chickens.',
          'Farmer: 10 cows.',
          '1 cow = 10 chickens.',
        ]);
        this.promptText.text = 'A: cows  B: chickens';
        break;
      case STEP.Era1Out:
        this.eraLabel.text = '9000 BC - OUTCOME';
        setBody(
          this.lastChoice === 'A'
            ? ['You took the cows.', 'Hard winter. 3 die.', 'Lesson: living', 'wealth dies.']
            : ['You kept chickens.', 'Plague spreads.', 'Lesson: living', 'wealth dies.'],
        );
        this.promptText.text = 'A: next';
        break;

      case STEP.Era2Choice:
        this.eraLabel.text = '1200 BC - SHELLS';
        setBody([
          'Merchant offers shells.',
          'Don\'t die. Hard to fake.',
          'Trade your livestock?',
        ]);
        this.promptText.text = 'A: shells  B: keep';
        break;
      case STEP.Era2Out:
        this.eraLabel.text = '1200 BC - OUTCOME';
        setBody(
          this.lastChoice === 'A'
            ? ['Smart trade.', 'But neighbors find', 'a shell beach. Market', 'floods. -15%.', 'Lesson: easy to find', '= easy to dilute.']
            : ['Kept livestock.', 'More plague. -40%.', 'Bad money loses', 'no matter what.'],
        );
        this.promptText.text = 'A: next';
        break;

      case STEP.Era3Choice:
        this.eraLabel.text = '600 BC - GOLD';
        setBody([
          'Lydian merchant:',
          '"Gold. Scarce.',
          ' Durable. Universal."',
          'But heavy. Slow.',
        ]);
        this.promptText.text = 'A: gold   B: pass';
        break;
      case STEP.Era3Out:
        this.eraLabel.text = '600 BC - OUTCOME';
        setBody(
          this.lastChoice === 'A'
            ? ['Gold +20%.', 'Real wealth now.', 'But heavy. Hard', 'to break into change.']
            : ['Passed on gold.', 'Shells flood again.', 'Lesson: scarcity', 'matters.'],
        );
        this.promptText.text = 'A: next';
        break;

      case STEP.Era4Choice:
        this.eraLabel.text = '1944 BRETTON WOODS';
        setBody([
          'US: give us your gold.',
          'Get dollars back.',
          '$35 = 1 oz of gold.',
          '"A promise."',
        ]);
        this.promptText.text = 'A: dollars  B: gold';
        break;
      case STEP.Era4Out:
        this.eraLabel.text = '1944 - OUTCOME';
        setBody(
          this.lastChoice === 'A'
            ? ['Took the deal.', 'Easier to use.', 'Feels safe.', '', '(remember 1971)']
            : ['Kept the gold.', 'Heavy. Yours.', 'Not a promise.', '', '(this matters soon)'],
        );
        this.promptText.text = 'A: next';
        break;

      case STEP.Nixon:
        this.eraLabel.text = '1971 - NIXON SHOCK';
        setBody(
          this.currentAsset === 'DOLLARS'
            ? ['Aug 15, 1971.', '"I am closing the', ' gold window."', 'Dollars not backed.', '-15%.']
            : ['Aug 15, 1971.', 'Gold window closed.', 'Promise broken.', 'You kept gold.', '+40%.'],
        );
        this.promptText.text = 'A: next';
        break;

      case STEP.Dilution:
        this.eraLabel.text = '2020-2024 DILUTION';
        setBody([
          '1971-2020: $15T in 49y',
          '2020-2024: $6T in 4y.',
          '40% of all dollars',
          'made in 4 years.',
          'Your pizza had 6 slices.',
          'Now it has 14.',
        ]);
        this.promptText.text = 'A: what now?';
        break;

      case STEP.FinalChoice:
        this.eraLabel.text = 'TODAY - YOUR MOVE';
        setBody([]);
        setOptions(FINAL_OPTIONS.map(o => o.label), this.finalCursor, 68);
        this.promptText.text = 'UP/DOWN  A: pick';
        break;

      case STEP.FinalOut: {
        const opt = this.finalChoice ?? 'DOLLARS';
        this.eraLabel.text = `YOU PICKED: ${opt}`;
        const out = FINAL_OUTCOMES[opt];
        setBody(out.lines);
        this.promptText.text = 'A: see your score';
        break;
      }

      case STEP.Score:
        this.eraLabel.visible = false;
        this.titleText.visible = true;
        this.titleText.text = `$${this.wealth}`;
        this.titleText.y = 65;  // below trophy sprite
        setBody([
          this.wealth >= 200 ? '"you read the room."' :
          this.wealth >= 100 ? '"you survived."' :
                                '"welcome to fiat."',
          '',
          'no money is perfect.',
        ]);
        // Push body below the title number
        this.bodyLines.forEach((t, i) => { t.y = 90 + i * 10; });
        this.promptText.text = 'A: continue';
        this.wealthText.visible = false;
        break;

      case STEP.Menu:
        this.eraLabel.text = 'GO DEEPER';
        setBody([
          'all fiat dilutes.',
          'all scarcity is a bet.',
          'speed matters.',
        ]);
        setOptions(['PLAY AGAIN'], 0, 90);
        this.promptText.text = 'A: replay';
        this.wealthText.visible = false;
        break;
    }
  }

  // ---------- One-time UI build ----------

  private build(): void {
    const root = new Container();
    this.addChild(root);

    // Era label (top-left)
    this.eraLabel = new Text({ text: '', style: { fontFamily: 'dogicapixel', fontSize: 7, fill: 0x000000 } });
    this.eraLabel.x = 4; this.eraLabel.y = 4;
    root.addChild(this.eraLabel);

    // Big title text
    this.titleText = new Text({ text: '', style: { fontFamily: 'tetris', fontSize: 16, fill: 0x000000 } });
    this.titleText.anchor.set(0.5, 0);
    this.titleText.x = SCREEN_W / 2; this.titleText.y = 30;
    this.titleText.visible = false;
    root.addChild(this.titleText);

    // Scene sprite (drawn programmatically per scene)
    this.sceneSprite = new Graphics();
    root.addChild(this.sceneSprite);

    // Body lines (centered text, 7 max)
    for (let i = 0; i < 7; i++) {
      const line = new Text({ text: '', style: { fontFamily: 'dogicapixel', fontSize: 7, fill: 0x000000, align: 'center' } });
      line.anchor.set(0.5, 0);
      line.x = SCREEN_W / 2;
      line.y = 70 + i * 10;
      line.visible = false;
      root.addChild(line);
      this.bodyLines.push(line);
    }

    // Option lines (for choice/menu screens)
    for (let i = 0; i < 6; i++) {
      const opt = new Text({ text: '', style: { fontFamily: 'dogicapixel', fontSize: 7, fill: 0x000000 } });
      opt.x = 18;
      opt.y = 60 + i * 10;
      opt.visible = false;
      root.addChild(opt);
      this.optionTexts.push(opt);
    }

    // Prompt at bottom
    this.promptText = new Text({ text: '', style: { fontFamily: 'dogicapixel', fontSize: 7, fill: 0x000000 } });
    this.promptText.anchor.set(0.5, 1);
    this.promptText.x = SCREEN_W / 2; this.promptText.y = SCREEN_H - 4;
    root.addChild(this.promptText);

    // Wealth display top-right
    this.wealthText = new Text({ text: '$100', style: { fontFamily: 'dogicapixel', fontSize: 7, fill: 0x000000 } });
    this.wealthText.anchor.set(1, 0);
    this.wealthText.x = SCREEN_W - 4; this.wealthText.y = 4;
    root.addChild(this.wealthText);

    this.isBuilt = true;
  }
}
